import axios from "axios";

/**
 * Google reCAPTCHA v3 for the Backoffice logins.
 *
 * Only the SITE key is here — it is public by design and domain-restricted in the reCAPTCHA console. The
 * secret half never leaves CagistaServices.
 *
 * `execute()` mints a token to SEND WITH the login. That is the whole point of this file now: the old
 * flow asked the backend "is this token good?" and then called the login separately, which is a gate in
 * the browser — a script skips the question and posts straight to the login. The token now travels with
 * the credentials and backoffice_login verifies it itself.
 */
const RECAPTCHA_SITE_KEY = "6LfYDhQdAAAAAOkHPvIPsQz3iVMMdAzXUFnXiRjz";

let scriptPromise = null;

function loadScript() {
    if (typeof grecaptcha !== "undefined") {
        return Promise.resolve();
    }
    if (!scriptPromise) {
        scriptPromise = new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = "https://www.google.com/recaptcha/api.js?render=" + RECAPTCHA_SITE_KEY;
            script.onload = resolve;
            script.onerror = () => {
                scriptPromise = null; // let the next attempt retry the load
                script.remove();
                reject(new Error("Failed to load reCaptcha script"));
            };
            document.head.appendChild(script);
        });
    }
    return scriptPromise;
}

class Recaptcha {
    /** Warm the script while the user is typing, so submitting does not wait on a network round trip. */
    preload() {
        if (import.meta.env.PROD) {
            loadScript().catch(() => {}); // failures are handled on submit
        }
    }

    /**
     * A scored token for `action`, to be sent with the request it protects.
     *
     * Resolves to null in DEV, because the site key does not cover localhost and there is no way to mint
     * one there. The backend's config.recaptchaEnforceLogin must be off for local development; it
     * defaults to ON so production is protected without anyone remembering to switch it.
     */
    execute(action) {
        if (import.meta.env.DEV) {
            return Promise.resolve(null);
        }
        return loadScript().then(() => new Promise((resolve, reject) => {
            grecaptcha.ready(() => {
                grecaptcha.execute(RECAPTCHA_SITE_KEY, { action }).then(resolve, reject);
            });
        }));
    }

    /**
     * Ask the backend to score a token on its own.
     *
     * Kept for callers that genuinely need a standalone verdict. It is NOT how a login should be
     * protected — see the note at the top of this file.
     */
    checkRecaptchaToken(token, action) {
        return axios.post('/api/public/checkRecaptchaToken', {
            recaptchaToken: token,
            recaptchaAction: action
        });
    }
}

export default new Recaptcha();
